/**
 * Un anillo de progreso. Sin texto, sin estado, sin saber de qué es.
 *
 * Existía en línea dentro de `ProductionTodayPanel`, y al necesitarlo la ficha
 * de impresora la alternativa era copiarlo — que es cómo dos anillos del mismo
 * producto acaban redondeando distinto.
 */
import React from 'react';

export interface ProgressRingProps {
    /** 0–100. **`null` no es 0**: un anillo vacío y uno al principio se dibujan
     *  igual, así que quien no tenga progreso no debería pintar un anillo. */
    percent: number | null;
    /** Grosor del trazo en unidades del `viewBox` (que es de 100×100). */
    stroke?: number;
    /** Clase del arco. El fondo siempre es neutro. */
    className?: string;
    children?: React.ReactNode;
}

const R = 44;
const C = 2 * Math.PI * R;

export default function ProgressRing({
    percent, stroke = 8, className = 'text-primary', children,
}: ProgressRingProps) {
    // Acotado, no confiado: una ruta que manda 120 dibujaría un arco por fuera
    // de su propio círculo, y una que manda -1 lo dibujaría del revés.
    const pct = Math.max(0, Math.min(100, percent ?? 0));

    // ⚠️ **CUADRADO, y atado a la ALTURA.**
    //
    // El `viewBox` es 100×100, así que esto tiene que ocupar una caja cuadrada.
    // Con `h-full w-full` dentro de un marco 4:3 el SVG se dibujaba al ANCHO del
    // marco —o sea más alto que el marco— y el `overflow-hidden` del padre le
    // cortaba arriba y abajo: se veía un anillo partido.
    //
    // `aspect-square h-full` lo hace tan ancho como alto y deja que la altura la
    // ponga el padre. `max-w-full` cubre la caja al revés —más alta que ancha—,
    // donde lo que sobraría sería el ancho.
    //
    // Y el padre tiene que tener una altura DEFINIDA. Como hijo normal de un
    // flex, esto se dimensiona por su contenido y su contenido por esto.
    return (
        <div className="relative flex aspect-square h-full max-w-full items-center justify-center">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden>
                <circle cx="50" cy="50" r={R} fill="none" stroke="currentColor" strokeWidth={stroke}
                        className="text-slate-200 dark:text-slate-700" />
                {percent != null ? (
                    <circle cx="50" cy="50" r={R} fill="none" stroke="currentColor" strokeWidth={stroke}
                            strokeDasharray={C} strokeDashoffset={C * (1 - pct / 100)}
                            strokeLinecap="round"
                            className={`${className} transition-all duration-500`} />
                ) : null}
            </svg>
            {children ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {children}
                </div>
            ) : null}
        </div>
    );
}
